/**
 * Functions that return or update drive app data only.
 */

import { API_PATHS } from "../../config";
import { apiRequest, getAPIData } from "./axios";

// Getters
export async function getDriveURL() {
  const url = "/files/geturl";
  return await getAPIData(url);
}

export async function getFolderInfo(path) {
  path = encodeURIComponent(path);
  const url = `/files/getfolder/${path}`;
  return await getAPIData(url);
}

/**
 * Resolve the Egnyte job folder path for deep-links (no child listing).
 *
 * @param {string} jobNumber - Job number used to resolve the Egnyte folder.
 * @returns {Promise<{ path: string, webUrl: string }|null>}
 */
export async function getJobFolderPath(jobNumber) {
  if (!jobNumber) {
    return null;
  }
  const url = `${API_PATHS.FILES.JOB_FOLDER}?job_number=${encodeURIComponent(jobNumber)}`;
  try {
    return await getAPIData(url);
  } catch (error) {
    console.error("Failed to resolve job folder path", error);
    return null;
  }
}

/**
 * Fetch curated project document folders with throttled Egnyte stats.
 * Prefer ``folderPath`` from ``getJobFolderPath`` to skip a second resolve.
 *
 * @param {Object} params
 * @param {string} [params.jobNumber] - Job number when path is unknown.
 * @param {string} [params.folderPath] - Absolute Egnyte job folder path.
 * @returns {Promise<{ path: string, webUrl: string, folders: Array }|null>}
 */
export async function getJobDocumentFolders({ jobNumber, folderPath } = {}) {
  const params = new URLSearchParams();
  if (folderPath) {
    params.set("folder_path", folderPath);
  } else if (jobNumber) {
    params.set("job_number", jobNumber);
  } else {
    return null;
  }

  const url = `${API_PATHS.FILES.JOB_DOCUMENT_FOLDERS}?${params.toString()}`;
  try {
    // Backend spaces Egnyte stats at ~2/sec; allow headroom past default 30s.
    return await apiRequest(url, null, { method: "GET", timeout: 60000 });
  } catch (error) {
    console.error("Failed to load project document folders", error);
    return null;
  }
}

export async function getFileLink(path) {
  path = encodeURIComponent(path);
  const url = `/files/getfile/${path}`;
  return await getAPIData(url);
}

export async function getMetadata(entry_id) {
  const url = `/files/getmetadata/${entry_id}`;
  return await getAPIData(url);
}

export async function downloadFile(path) {
  path = encodeURIComponent(path);
  const url = `/files/downloadfile/${path}`;
  return await getAPIData(url);
}
